import type { FC } from 'react';

import type { Account } from '@shared/products-types';
import { t } from '@luna/i18n';
import { useNotifications } from '@shared/snackbar';
import { georgeStats } from '@shared/statistics';
import type { ProductWithHelpers } from '@shared/products-core';
import { AliasChangeDropdown } from '@retail/products-customisation';
import { getDefaultTitle, getTitleAlignedWithGeorgeApp } from '@shared/products-general-service';

import { useProductData } from '../../hooks/useProductData';
import { CardMenuDropdownToggle } from '../../overview-actions-dropdown/CardMenuDropdownToggle';

interface Props {
    account: ProductWithHelpers<Account>;
    onSave?: () => void;
}

export const NameChangeDropdown: FC<Props> = ({ account, onSave }) => {
    const { updateAlias } = useProductData(account.id);
    const { displayError } = useNotifications('overview-dropdown-actions');

    const handleAliasChangeError = (error: Error) => {
        displayError(t('mainOverview.action.changeName_error'));
        georgeStats.trackEvent('overview', 'box_saveNameChange_error', {
            error: error?.toString(),
        });
    };

    const handleAliasChangeSuccess = () => {
        georgeStats.trackEvent('overview', 'box_saveNameChange', { accountType: account.type });
        onSave?.();
    };

    const hasAlias = Boolean(account.alias);

    return (
        <AliasChangeDropdown
            alias={getTitleAlignedWithGeorgeApp(account)}
            hasAlias={hasAlias}
            onAliasChangeSuccess={handleAliasChangeSuccess}
            onAliasChangeError={handleAliasChangeError}
            aliasChangeContext="dashboard"
            onAliasSave={updateAlias}
            productType={account.type}
            toggle={<CardMenuDropdownToggle data-cy="name-change-toggle-dropdown" />}
            placeholder={getDefaultTitle(account)}
            trigger="overview"
        />
    );
};
